import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useSeo from '@/hooks/useSeo';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import VerticalTimeline from '@/pages/timeline/detail/components/VerticalTimeline';
import RelatedEvents from '@/pages/timeline/detail/components/RelatedEvents';
import { events } from '@/mocks/events';
import { formatHeat, trendClass, trendLabel } from '@/utils/format';

const SITE_URL = 'https://captain2016.cn';

const sections = [
  { id: 'timeline', label: '事件时间线' },
  { id: 'cause', label: '前因' },
  { id: 'process', label: '经过' },
  { id: 'result', label: '当后果' },
  { id: 'related', label: '相关事件' },
];

export default function TimelineDetail() {
  const { id } = useParams<{ id: string }>();
  const event = events.find((item) => item.id === id);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCopied(false);
  }, [id]);

  useSeo({
    title: event
      ? `${event.title} — 时间线与来龙去脉深度拆解 | 蝌蚪吃瓜`
      : '事件未找到 — 蝌蚪吃瓜',
    description: event
      ? `${event.summary} 完整时间线、前因、经过与当后果一次梳理清楚。`
      : '该事件暂未收录，返回事件合集查看其他时间线。',
    keywords: event
      ? `${event.categoryName},${event.tags.join(',')},蝌蚪吃瓜时间线,前因后果`
      : '蝌蚪吃瓜,事件合集',
    path: `/timeline/${id ?? ''}`,
    jsonLd: event
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: event.title,
            description: event.summary,
            image: event.cover,
            datePublished: event.date,
            dateModified: event.date,
            keywords: event.tags.join(','),
            articleSection: event.categoryName,
            author: { '@type': 'Organization', name: '蝌蚪吃瓜' },
            publisher: { '@type': 'Organization', name: '蝌蚪吃瓜' },
            mainEntityOfPage: `${SITE_URL}/timeline/${event.id}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_URL}/` },
              { '@type': 'ListItem', position: 2, name: '事件合集', item: `${SITE_URL}/timeline` },
              { '@type': 'ListItem', position: 3, name: event.title, item: `${SITE_URL}/timeline/${event.id}` },
            ],
          },
        ]
      : undefined,
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!event) {
    return (
      <div className="mx-auto flex w-full flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
          <i className="ri-file-unknow-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          这条时间线还没有被整理
        </h1>
        <p className="mt-3 max-w-lg text-sm leading-relaxed text-foreground-600">
          你要查看的事件暂未收录，可能链接有误或内容正在整理中。可以回到事件合集，看看当前最值得关注的几件事。
        </p>
        <Link
          to="/timeline"
          className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line" />
          返回事件合集
        </Link>
      </div>
    );
  }

  return (
    <article className="bg-background-50">
      <header className="border-b border-background-200 bg-background-100 py-8 md:py-12">
        <div className="mx-auto w-full px-4 md:px-6">
          <Breadcrumbs
            items={[
              { label: '事件合集', to: '/timeline' },
              { label: event.categoryName, to: `/timeline?category=${event.category}` },
              { label: '事件详情' },
            ]}
          />
          <div className="mt-5 max-w-3xl">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/timeline?category=${event.category}`}
                className="rounded-full bg-primary-100 px-3 py-1 font-label text-xs text-primary-700 transition-colors hover:bg-primary-200"
              >
                {event.categoryName}
              </Link>
              <span className={`rounded-full px-3 py-1 font-label text-xs ${trendClass(event.trend)}`}>
                {trendLabel(event.trend)}
              </span>
              <span className="flex items-center gap-1 font-label text-xs text-foreground-500">
                <i className="ri-fire-line text-primary-500" />
                {formatHeat(event.heat)} 热度
              </span>
            </div>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              {event.title}
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              {event.summary}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-label text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                最后更新 {event.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-git-commit-line" />
                {event.timeline.length} 个时间节点
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" />
                约 {event.readTime}
              </span>
              <button
                type="button"
                onClick={handleCopy}
                className="flex items-center gap-1 rounded-md border border-background-300 px-2.5 py-1 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className={copied ? 'ri-check-line' : 'ri-link'} />
                {copied ? '链接已复制' : '复制链接'}
              </button>
            </div>
          </div>
          <div className="mt-8 h-56 w-full overflow-hidden rounded-lg md:h-80">
            <img
              src={event.cover}
              alt={`${event.title} - 蝌蚪吃瓜事件时间线配图`}
              title={`${event.title} - 蝌蚪吃瓜时间线`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>
      </header>

      <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="flex flex-col gap-12 lg:col-span-8">
            <section id="timeline" className="scroll-mt-28">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-timeline-view text-lg" />
                </span>
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">事件时间线</h2>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                以下节点按发生时间先后排列，标签标明了每个节点所处的阶段，方便你定位事情是在哪一步开始变化。
              </p>
              <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <VerticalTimeline nodes={event.timeline} />
              </div>
            </section>

            <section id="cause" className="scroll-mt-28">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-seedling-line text-lg" />
                </span>
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">前因</h2>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-[15px]">{event.cause}</p>
            </section>

            <section id="process" className="scroll-mt-28">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className="ri-route-line text-lg" />
                </span>
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">经过</h2>
              </div>
              <ol className="mt-5 flex flex-col gap-4">
                {event.process.map((step, index) => (
                  <li key={step} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-foreground-950 font-heading text-sm font-bold text-background-50">
                      {index + 1}
                    </span>
                    <p className="pt-1 text-sm leading-relaxed text-foreground-700">{step}</p>
                  </li>
                ))}
              </ol>
            </section>

            <section id="result" className="scroll-mt-28">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-flag-2-line text-lg" />
                </span>
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">当后果</h2>
              </div>
              <div className="mt-4 rounded-lg border-l-4 border-primary-500 bg-primary-50 p-5">
                <p className="text-sm leading-relaxed text-foreground-700 md:text-[15px]">{event.result}</p>
              </div>
            </section>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 lg:space-y-6">
              <nav
                aria-label="本篇目录"
                className="rounded-lg border border-background-200 bg-background-100 p-5"
              >
                <h2 className="font-heading text-sm font-bold text-foreground-950">本篇目录</h2>
                <ul className="mt-3 space-y-2">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <a
                        href={`#${section.id}`}
                        className="flex items-center gap-2 font-label text-sm text-foreground-600 transition-colors hover:text-primary-600"
                      >
                        <i className="ri-arrow-right-s-line text-foreground-400" />
                        {section.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 lg:mt-0">
                <h2 className="font-heading text-sm font-bold text-foreground-950">关键信息</h2>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">所属栏目</dt>
                    <dd className="font-label text-foreground-900">{event.categoryName}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">节点数量</dt>
                    <dd className="font-label text-foreground-900">{event.timeline.length} 个</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">当前热度</dt>
                    <dd className="font-label text-foreground-900">{formatHeat(event.heat)}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">最后更新</dt>
                    <dd className="font-label text-foreground-900">{event.date}</dd>
                  </div>
                </dl>
                <div className="mt-4 flex flex-wrap gap-2 border-t border-background-200 pt-4">
                  {event.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary-50 px-2.5 py-1 font-label text-xs text-secondary-800"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 p-5 lg:mt-0">
                <div className="flex items-center gap-2 text-primary-600">
                  <i className="ri-error-warning-line" />
                  <span className="font-label text-sm">发现信息需要更正？</span>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  欢迎附上信息出现的时间与来源，我们会先核对再决定是补充节点还是修订已有内容。
                </p>
                <Link
                  to="/contact"
                  className="mt-3 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2 font-label text-xs text-background-50 transition-colors hover:bg-foreground-800"
                >
                  提交更正线索
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section id="related" className="scroll-mt-28 border-t border-background-200 bg-background-100 py-12 md:py-16">
        <div className="mx-auto w-full px-4 md:px-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关事件</h2>
              <p className="mt-2 text-sm text-foreground-600">
                这些时间线与本篇在话题或行业背景上有关联，适合一起看。
              </p>
            </div>
            <Link
              to={`/timeline?category=${event.category}`}
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 font-label text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              浏览同栏目
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="mt-6">
            <RelatedEvents ids={event.related} />
          </div>
        </div>
      </section>
    </article>
  );
}